// EmailEditorTinyMCE.js
import React, { useState, useEffect, useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { STyledTextEditor } from './TextEditor.styles';
import Button from '../../molecules/Button';
import Select from '../../molecules/Select';
import { useLocation } from 'react-router-dom';
import Loaders from '../Loaders';
import { HiOutlineEye } from 'react-icons/hi2';
import { SPINTAX_TEMPLATES } from '../../Constant';




const TextEditor = ({
  data,
  subject,
  setSubject,
  body,
  setBody,
  setUnsubscribeOption,
  unsubscribeOption,
  defaultLocked = false,
}) => {
  const location = useLocation();
  const [editorLoading, setEditorLoading] = useState(false);
  const [selectedVariables, setSelectedVariables] = useState([]);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const variableSelectRef = useRef(null);
  const editorRef = useRef(null);
  const unsubscribeOptionRef = useRef(unsubscribeOption);


  const predefinedFields = [
    { key: 'first_name' },
    { key: 'last_name' },
    { key: 'email' },
    { key: 'website' },
    { key: 'location' },
    { key: 'title' },
    { key: 'company_name' },
    { key: 'industry' },
    { key: 'technologies' },
    { key: 'message_one' },
    { key: 'subject_one' },
    { key: 'message_two' },
    { key: 'subject_two' },
    { key: 'message_three' },
    { key: 'subject_three' },
    { key: 'greeting' },
  ];

  const isHoudinyLeads = data?.isHoudinyLeads;

  const items =
    predefinedFields?.map(item => ({
      label: `{{${item.key}}}`,
      value: item.key,
      raw: item.key,
    })) || [];

  const subjectItems = items.filter(item => item.value !== 'greeting');

  useEffect(() => {
    const handleClickOutside = e => {
      if (variableSelectRef.current && !variableSelectRef.current.contains(e.target)) {
        // Also check if click is on the editor container
        const editorContainer = document.querySelector('.editor-holder');
        if (editorContainer && editorContainer.contains(e.target)) {
          setMenuIsOpen(false);
        } else {
          setMenuIsOpen(false);
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Keep ref in sync with unsubscribeOption prop
  // This ensures the menu always shows the correct selected value, even after data loads
  useEffect(() => {
    unsubscribeOptionRef.current = unsubscribeOption;
  }, [unsubscribeOption]);

  useEffect(() => {
    if (!subject) {
      setSelectedVariables([]);
      return;
    }

    const variablePattern = /\{\{(\w+)\}\}/g;
    const matches = [];
    let match;

    while ((match = variablePattern.exec(subject)) !== null) {
      matches.push(match[1]);
    }

    // Find matching items from the subjectItems array (for subject dropdown)
    const foundItems = subjectItems.filter(item => matches.includes(item.value));
    setSelectedVariables(foundItems);
  }, [subject]);

  return (
    <STyledTextEditor>
      <div className="editor-head">
        <label>Subject</label>
        <input type="text" value={subject} onChange={e => setSubject(e.target.value)} disabled={defaultLocked} />
        <div className="variables-select" ref={variableSelectRef}>
          {selectedVariables?.length > 0 && (
            <span className="variables-select-placeholder" onClick={() => !defaultLocked && setMenuIsOpen(true)}>
              Select variables...
            </span>
          )}
          <Select
            options={subjectItems}
            value={selectedVariables}
            placeholder="Select variables"
            menuIsOpen={menuIsOpen}
            onMenuOpen={() => !defaultLocked && setMenuIsOpen(true)}
            onMenuClose={() => setMenuIsOpen(false)}
            onChange={e => {
              if (defaultLocked) return;
              const selectedValues = e.target.value || [];
              setSelectedVariables(selectedValues);

              if (Array.isArray(selectedValues) && selectedValues.length > 0) {
                // Join multiple selected subjects with spaces
                const subjectString = selectedValues.map(item => item.label).join(' ');
                setSubject(subjectString);
              } else {
                // Clear subject when all values are removed
                setSubject('');
              }
            }}
            noMargin
            isMulti
            disabled={defaultLocked}
          />
        </div>
        {/* <div className="save-btn"> */}
        {/* <Button rounded className="btn-preview" width="150px">
            <HiOutlineEye size={20} />
            Preview
          </Button>
          <Button rounded width="150px">
            Save Step
          </Button> */}
        {/* </div> */}
      </div>
      <div className="editor-holder">
        {editorLoading ? (
          <Loaders viewLoader />
        ) : (
          <>
            {/* {readOnly || lockMessage ? (
              <div className="lock-message">{lockMessage}</div>
            ) : ( */}
            <Editor
              apiKey="3v80rwqhpqj0r6x4kbc780fzud8p1ssm0uzelnn1vjavhbus"
              value={body}
              onEditorChange={newValue => {
                if (!defaultLocked) {
                  setBody(newValue);
                }
                setMenuIsOpen(false);
              }}
              onInit={(evt, editor) => {
                editorRef.current = editor;
                setEditorLoading(false);
                // Close dropdown when clicking in the editor
                editor.on('click', () => {
                  setMenuIsOpen(false);
                });
                editor.on('focus', () => {
                  setMenuIsOpen(false);
                });
              }}
              init={{
                readonly: defaultLocked,
                menubar: false,
                statusbar: false,
                placeholder: 'Start typing here...',
                plugins: [
                  'advlist',
                  'autolink',
                  'lists',
                  'link',
                  'image',
                  'charmap',
                  'preview',
                  'anchor',
                  'searchreplace',
                  'visualblocks',
                  'code',
                  'fullscreen',
                  'insertdatetime',
                  'media',
                  'table',
                  'paste',
                  'help',
                  'wordcount',
                ],
                content_style: `
                  body {
                    margin: 0 !important;
                    padding: 16px 0 !important;
                    font-family: "DM Sans", sans-serif;
                  }
                  p {
                    margin: 0 !important;
                  }
                  .signature {
                    display: block;
                    font-size: 18px;
                    line-height: 22px;
                    font-weight: 700;
                    color: #313131;
                    font-style: italic;
                  }
                `,
                toolbar:
                  'undo redo | formatselect fontselect fontsizeselect | ' +
                  'bold italic underline strikethrough forecolor | variables spintax unsubscribe ' +
                  'alignleft aligncenter alignright alignjustify | ' +
                  'bullist numlist outdent indent | link image  | removeformat | preview code help',

                setup: editor => {
                  editor.ui.registry.addMenuButton('unsubscribe', {
                    text: 'Unsubscribe',
                    fetch: callback => {
                      let currentValue = unsubscribeOptionRef.current ?? unsubscribeOption;
                      if (currentValue === 'true' || currentValue === true || currentValue === 1) {
                        currentValue = true;
                      } else if (currentValue === 'false' || currentValue === false || currentValue === 0) {
                        currentValue = false;
                      } else {
                        currentValue = !!currentValue;
                      }
                      const isTrueSelected = currentValue === true;
                      const isFalseSelected = currentValue === false;

                      const checkmark = '✓';

                      callback([
                        {
                          type: 'menuitem',
                          text: isTrueSelected ? `${checkmark} True` : 'True',
                          onAction: () => {
                            if (!defaultLocked) {
                              // Update ref immediately for instant UI feedback
                              unsubscribeOptionRef.current = true;
                              setUnsubscribeOption(true);
                            }
                          },
                        },
                        {
                          type: 'menuitem',
                          text: isFalseSelected ? `${checkmark} False` : 'False',
                          onAction: () => {
                            if (!defaultLocked) {
                              // Update ref immediately for instant UI feedback
                              unsubscribeOptionRef.current = false;
                              setUnsubscribeOption(false);
                            }
                          },
                        },
                      ]);
                    },
                  });

                  editor.ui.registry.addMenuButton('variables', {
                    text: 'Insert Variable',
                    fetch: callback => {
                      const menuItems = (items || []).map(item => ({
                        type: 'menuitem',
                        text: item?.label || '',
                        onAction: () => {
                          if (!defaultLocked) {
                            editor.insertContent(item?.label || '');
                          }
                        },
                      }));

                      callback(menuItems);
                    },
                  });

                  editor.ui.registry.addMenuButton('spintax', {
                    text: 'Spintax',
                    fetch: callback => {
                      const menuItems = SPINTAX_TEMPLATES?.map(template => ({
                        type: 'menuitem',
                        text: template.label,
                        onAction: () => {
                          if (!defaultLocked) {
                            editor.insertContent(template.content);
                          }
                        },
                      }));

                      callback(menuItems);
                    },
                  });
                },
              }}
            />
            {/* )} */}
          </>
        )}
      </div>
    </STyledTextEditor>
  );
};

export default TextEditor;
